import { DefaultOptionType } from '@bugly/common';
import { CharacterInput, CustomInputRenderProps, CharacterInputValue } from '@tencent/tds-ui';

import { useControllableValue } from 'ahooks';
import { SelectProps } from 'antd';
import classNames from 'classnames';
import { isArray, isPlainObject } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { TdsCharacterRange } from '@/presentation/components/tds-form/tds-character-range';
import { TdsFieldSelector, TdsFieldSelectorProps } from '@/presentation/components/tds-form/tds-field-selector';
import { BaseFormItemProps, PropsWithStyles } from '@/presentation/components/tds-form/type';
import styles from './index.module.scss';

export interface TdsCharacterInputProps<T = any> extends BaseFormItemProps<T> {
  modeSelectProps?: SelectProps;
  valueSelectProps?: TdsFieldSelectorProps;
  mode: 'multiple' | 'tags' | 'single';
  modeOptions?: DefaultOptionType[];
  defaultActiveFirstMode?: boolean;
}

/**
 * 只允许单选的模式
 */
const SINGLE_INPUT_MODE = [
  MatchMode.Le,
  MatchMode.Lt,
  MatchMode.Lt,
  MatchMode.Ge,
  MatchMode.Gt,
];

/**
 * 不允许输入的模式
 */
const DISABLED_INPUT_MODE = [
  MatchMode.ISEMPTY,
  MatchMode.NOTEMPTY,
];

export const TdsCharacterInput = (props: PropsWithStyles<TdsCharacterInputProps>) => {
  const [value, setValue] = useControllableValue<CharacterInputValue>(props, {
    defaultValue: {
      mode: undefined,
      values: undefined,
    },
  });
  const {
    valueSelectProps,
    modeSelectProps,
    mode,
    modeOptions,
    defaultActiveFirstMode = true,
  } = props;

  const formatAntdMode = (mode: string) => (mode !== 'single' ? mode : undefined);

  const [valueMode, setValueMode] = useState<any>(formatAntdMode(mode));
  const ref = useRef<any>();

  const valueDisabled = useMemo(() => DISABLED_INPUT_MODE.includes(value?.mode as MatchMode), [value?.mode]);

  const customInputRender = (params: CustomInputRenderProps) => {
    if (value.mode === MatchMode.Between) {
      return <TdsCharacterRange
        valueSelectProps={valueSelectProps}
        value={params.value}
        onChange={params.onChange}
      />;
    }

    return <TdsFieldSelector
      showArrow={false}
      {...valueSelectProps}
      disabled={valueDisabled}
      mode={valueMode}
      style={{ flex: 1 }}
      value={ params.value}
      onChange={params.onChange}
    />;
  };

  useEffect(() => {
    if (!value?.mode) {
      return;
    }
    if (MatchMode.Between === value?.mode) {
      const value: CharacterInputValue = ref.current?.getValue();
      const nextValue = value?.values;
      ref.current?.onValueChange(nextValue);
      return;
    }

    if (SINGLE_INPUT_MODE.includes(value?.mode as MatchMode)) {
      const value: CharacterInputValue = ref.current?.getValue();
      let nextValue = value?.values;
      if (isPlainObject(value?.values)) {
        nextValue = undefined;
      } else if (isArray(value?.values)) {
        nextValue = value?.values?.[0];
      }
      ref.current?.onValueChange(nextValue);
      setValueMode(undefined);
    } else {
      const value: CharacterInputValue = ref.current?.getValue();
      const nextValue = isPlainObject(value?.values) ? [] : value?.values;
      ref.current?.onValueChange(nextValue);
      setValueMode(formatAntdMode(mode));
    }

    if (DISABLED_INPUT_MODE.includes(value?.mode as MatchMode)) {
      ref.current?.onValueChange(undefined);
    }
  }, [value?.mode]);

  return (
    <CharacterInput
      className={classNames(styles.tdsSelector, props.className)}
      style={props.style}
      ref={ref}
      value={value}
      defaultActiveFirstMode={defaultActiveFirstMode}
      modeSelectProps={{
        dropdownMatchSelectWidth: 100,
        options: modeOptions,
        ...modeSelectProps,
      }}
      customInputRender={customInputRender}
      onChange={(value) => {
        setValue(value);
      }}
    />
  );
};
